export interface StripeRequestContext {
  stripeRequestId: number;
  stripeRequestLoading: boolean;
}

export interface StripeStatusRequestContext {
  stripeStatusRequestId: number;
  stripeStatusLoading: boolean;
}

interface LoadCurrentStripeStatusOptions<T> {
  context: StripeStatusRequestContext;
  entityId: number;
  selectedEntityId: () => number | null | undefined;
  fetchStatus: () => Promise<T>;
  applyStatus: (status: T) => void;
  onError: (error: unknown) => void;
}

export function beginStripeRequest(context: StripeRequestContext): number {
  context.stripeRequestId += 1;
  context.stripeRequestLoading = true;
  return context.stripeRequestId;
}

export function invalidateStripeRequest(context: StripeRequestContext): void {
  context.stripeRequestId += 1;
  context.stripeRequestLoading = false;
}

export function isCurrentStripeRequest(
  context: StripeRequestContext,
  requestId: number
): boolean {
  return context.stripeRequestId === requestId;
}

export function finishStripeRequest(
  context: StripeRequestContext,
  requestId: number
): void {
  if (isCurrentStripeRequest(context, requestId)) {
    context.stripeRequestLoading = false;
  }
}

export function invalidateStripeStatusRequest(
  context: StripeStatusRequestContext
): void {
  context.stripeStatusRequestId += 1;
  context.stripeStatusLoading = false;
}

export async function loadCurrentStripeStatus<T>(
  options: LoadCurrentStripeStatusOptions<T>
): Promise<void> {
  const requestId = options.context.stripeStatusRequestId + 1;
  options.context.stripeStatusRequestId = requestId;
  options.context.stripeStatusLoading = true;
  const isCurrent = () =>
    options.context.stripeStatusRequestId === requestId &&
    options.selectedEntityId() === options.entityId;

  try {
    const status = await options.fetchStatus();
    if (isCurrent()) options.applyStatus(status);
  } catch (error) {
    if (isCurrent()) options.onError(error);
  } finally {
    if (isCurrent()) options.context.stripeStatusLoading = false;
  }
}
